// Carrier errors are stored on the activity log as
// `params: { carrierCode, category, rawMessage }`. `category` maps to a
// translatable merchant message; `rawMessage` is kept for support/debugging.
type CarrierErrorParams = {
  carrierCode: string;
  category: string;
  rawMessage: string;
};

function getCarrierErrorParams(params: unknown): CarrierErrorParams | null {
  if (
    params === null ||
    typeof params !== "object" ||
    !("category" in params) ||
    !("rawMessage" in params)
  ) {
    return null;
  }

  const record = params as Record<string, unknown>;
  if (
    typeof record.category !== "string" ||
    typeof record.rawMessage !== "string"
  ) {
    return null;
  }

  return {
    carrierCode:
      typeof record.carrierCode === "string" ? record.carrierCode : "",
    category: record.category,
    rawMessage: record.rawMessage,
  };
}

const CATEGORY_TRANSLATION_KEY: Record<string, string> = {
  "order-state": "errors.carrier.orderState",
  "network-unavailable": "errors.carrier.network",
  auth: "errors.carrier.auth",
  "carrier-config": "errors.carrier.carrierConfig",
  "address-recipient": "errors.carrier.addressRecipient",
  "parcel-data": "errors.carrier.parcelData",
};

// null for unknown/unmapped categories — the caller falls back to the raw message.
function getCarrierErrorTranslationKey(category: string): string | null {
  return CATEGORY_TRANSLATION_KEY[category] ?? null;
}

// Translated category if known, else the raw message; `legacyFallback` covers
// old logs written before structured params existed.
export function resolveCarrierErrorMessage(
  params: unknown,
  t: (key: string) => string,
  legacyFallback: () => string | null | undefined,
): string | null | undefined {
  const carrierParams = getCarrierErrorParams(params);
  if (carrierParams) {
    const key = getCarrierErrorTranslationKey(carrierParams.category);
    return key ? t(key) : carrierParams.rawMessage;
  }

  return legacyFallback();
}
